<template>
  <div class="paging">
    <div class="paging-header subnavbar-header" data-test="sm-paging-header">
      Page {{ currentPageNumber }} / {{ maxPageNumber }}
    </div>
    <div
      class="paging-body"
      :class="[
        'page-number-' +
          (currentPageNumber - minPageNumber + 1) +
          '-of-' +
          (maxAccessiblePageNumber - minPageNumber + 1),
      ]"
    >
      <div class="paging-item-wrapper">
        <!--        <a class="page-arrow" @click="previousPage" :disabled="!canGoToPreviousPage"-->
        <!--          ><span class="glyphicon glyphicon-menu-left"></span-->
        <!--        ></a>-->
      </div>
      <div class="paging-item-wrapper" v-for="page in pageDetails" v-bind:key="page.pageNumber">
        <a
          class="page-number"
          @click="goToPage(page.pageNumber)"
          @keydown.enter="goToPage(page.pageNumber)"
          :key="page.pageNumber"
          :disabled="!page.isAvailable"
          :class="{ current: page.isCurrent }"
          >{{ page.pageNumber }}</a
        >
      </div>
      <div class="paging-item-wrapper">
        <!--        <a class="page-arrow" @click="nextPage" :disabled="!canGoToNextPage"-->
        <!--          ><span class="glyphicon glyphicon-menu-right"></span-->
        <!--        ></a>-->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PageDetail } from "../../views/strengths-mapping/strengths-mapping.vue";

const props = defineProps<{
  currentPageNumber: number;
  minPageNumber: number;
  maxPageNumber: number;
  maxAccessiblePageNumber: number;
  canGoToPreviousPage: boolean;
  canGoToNextPage: boolean;
  pageDetails: PageDetail[];
}>();

const emit = defineEmits(["request-save-and-exit", "previous-page", "next-page", "go-to-page"]);

function saveAndExit(): void {
  emit("request-save-and-exit");
}

function previousPage(): void {
  emit("previous-page");
}

function nextPage(): void {
  emit("next-page");
}

function goToPage(pageNumber: number): void {
  emit("go-to-page", pageNumber);
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import "./strengths-mapping-variables.less";
@import (reference) "../../styles/bootstrap/variables.less";

@paging-width: @subnavbar-font-size * 12;
@paging-header-padding: @subnavbar-header-padding;
@paging-circle-dimension: @subnavbar-font-size * 1.5;
@paging-circle-margin-top: -@paging-circle-dimension / 2 - 0.08em; // 0.08em is specific to the font: it's the actual difference between 1em and the height of a number
@paging-circle-margin-left: -@paging-circle-dimension / 2;
@mobile-paging-background-color: white;
.paging {
  width: @paging-width;
  border-left: @navbar-border-color @navbar-border-width solid;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  &.mobile-paging {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background-color: @mobile-paging-background-color;
    z-index: @z-index-mobile-paging;
    transition: left 0.5s;
    border-left: none;
    border-right: @navbar-border-color @navbar-border-width solid;
    .paging-header {
      display: none;
    }
    .paging-body {
      a {
        .strengths-mapping:not(.is-page-complete) & {
          transition: none; // Prevent odd transitions when hiding/showing the paging
        }
      }
    }
    .is-page-complete & {
      left: 0;
    }
  }
}
.paging-header {
  width: 100%;
  // .subnavbar-header-height();
  text-align: center;
  background-color: @off-white;
  border-bottom: 1px solid @paging-header-border-color; // To match rest of header height
  color: @paging-header-color;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}
.paging-body {
  flex: 10;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  font-size: 130%;
  position: relative; // For absolute positioning of the circle
  .paging-item-wrapper {
    flex: 10; // Fill the available space
    overflow: visible;
    text-align: center;
    z-index: 2;
    line-height: 0;
  }
  span {
    display: inline-block;
  }
  a {
    transition: color 0.3s, opacity 0.3s;
    text-decoration: none;
    cursor: pointer;
    color: @charismatic-color;
    &[disabled] {
      color: @disabled-link-color;
      cursor: not-allowed;
    }
    &:hover {
      color: @charismatic-color;
    }
    &.page-arrow span {
      line-height: 0;
      transform: scaleY(1.2);
    }
    &.page-arrow[disabled] {
      opacity: 0;
    }
  }
  .page-number.current {
    color: white;
  }
  &::before {
    content: " ";
    display: inline-block;
    border-radius: 50%;
    background-color: @charismatic-color;
    position: absolute;
    top: 50%;
    margin-top: @paging-circle-margin-top;
    height: @paging-circle-dimension;
    width: @paging-circle-dimension;
    z-index: 1; // Behind text but in front of background
    margin-left: @paging-circle-margin-left;
    transition: left 0.5s;
  }
  .page-number-position(@number, @max-page-number) {
    left: 100% * (1 + 2 * @number) / (2 * (@max-page-number + 2));
  }
  &.page-number-1-of-1::before {
    .page-number-position(1, 1);
  }
  &.page-number-1-of-2::before {
    .page-number-position(1, 2);
  }
  &.page-number-2-of-2::before {
    .page-number-position(2, 2);
  }
  &.page-number-1-of-3::before {
    .page-number-position(1, 3);
  }
  &.page-number-2-of-3::before {
    .page-number-position(2, 3);
  }
  &.page-number-3-of-3::before {
    .page-number-position(3, 3);
  }
  &.page-number-1-of-4::before {
    .page-number-position(1, 4);
  }
  &.page-number-2-of-4::before {
    .page-number-position(2, 4);
  }
  &.page-number-3-of-4::before {
    .page-number-position(3, 4);
  }
  &.page-number-4-of-4::before {
    .page-number-position(4, 4);
  }
  &.page-number-1-of-5::before {
    .page-number-position(1, 5);
  }
  &.page-number-2-of-5::before {
    .page-number-position(2, 5);
  }
  &.page-number-3-of-5::before {
    .page-number-position(3, 5);
  }
  &.page-number-4-of-5::before {
    .page-number-position(4, 5);
  }
  &.page-number-5-of-5::before {
    .page-number-position(5, 5);
  }
  &.page-number-1-of-6::before {
    .page-number-position(1, 6);
  }
  &.page-number-2-of-6::before {
    .page-number-position(2, 6);
  }
  &.page-number-3-of-6::before {
    .page-number-position(3, 6);
  }
  &.page-number-4-of-6::before {
    .page-number-position(4, 6);
  }
  &.page-number-5-of-6::before {
    .page-number-position(5, 6);
  }
  &.page-number-6-of-6::before {
    .page-number-position(6, 6);
  }
}
</style>
